// AfterTide home page data — dark/mature themed variants of LitLaunch home data
import {
  Book,
  BookBox,
  MerchProduct,
  TroveArticle,
  Trope,
  ReaderReview,
  UpcomingEvent,
  SpotlightAuthor,
} from './homePageData'

// AfterTide promotional banners (used by CustomPromotionalBannerCarousel)
export const aftertidePromotionalBanners = [
  {
    id: 1,
    title: 'New Tier 5 Releases',
    description: 'Explore the darkest new additions to the catalog',
    bgColor: 'bg-gradient-to-r from-[#1a1a2e] to-[#16213e]',
    textColor: 'text-[#e2b04a]',
    link: '/browse?tier=5',
  },
  {
    id: 2,
    title: 'Dark Romance Week',
    description: 'Curated picks from our most intense authors',
    bgColor: 'bg-gradient-to-r from-[#2d1b3d] to-[#1a1a2e]',
    textColor: 'text-[#c77dba]',
    link: '/browse?genre=dark-romance',
  },
  {
    id: 3,
    title: 'Monster Romance Collection',
    description: '142 titles — tentacles, fangs, and everything in between',
    bgColor: 'bg-gradient-to-r from-[#16213e] to-[#0f3460]',
    textColor: 'text-[#e2b04a]',
    link: '/browse?trope=monster-romance',
  },
  {
    id: 4,
    title: 'The Midnight Dispatch',
    description: 'Subscribe for exclusive dark fiction recommendations',
    bgColor: 'bg-gradient-to-r from-[#1a1a2e] to-[#2d1b3d]',
    textColor: 'text-[#f5f0e8]',
  },
]

export const aftertideFeatureCards = [
  {
    title: 'Darkness Tiers',
    description:
      'Navigate content intensity with our 5-tier classification system.',
    bgColor: 'bg-gradient-to-r from-[#e2b04a]/80 to-[#d1a03a]',
    textColor: 'text-[#1a1a2e]',
    link: '/browse?featured=tier-guide',
  },
  {
    title: 'Consent Frameworks',
    description: 'Clear labeling of consent dynamics in every title.',
    bgColor: 'bg-gradient-to-r from-[#c77dba] to-[#a855a0]',
    textColor: 'text-[#1a1a2e]',
    link: '/browse?featured=consent-guide',
  },
  {
    title: 'Progressive Warnings',
    description:
      'Spoiler-free content warnings with optional detailed breakdowns.',
    bgColor: 'bg-gradient-to-r from-[#16213e] to-[#0f3460]',
    textColor: 'text-[#e2b04a]',
    link: '/browse?featured=warnings-guide',
  },
]

// Platform features for "What is AfterTide" section
export const aftertidePlatformFeatures = [
  {
    id: 'tier-system',
    title: 'Darkness Tiers',
    description:
      'A 5-tier system rating emotional and psychological intensity, from Mild to Extreme.',
    iconName: 'tag' as const,
  },
  {
    id: 'consent-framework',
    title: 'Consent Frameworks',
    description:
      'Clear labeling of consent dynamics: Enthusiastic, Dubious, Non-Consent, and more.',
    iconName: 'users' as const,
  },
  {
    id: 'progressive-warnings',
    title: 'Progressive Warnings',
    description:
      'High-level warnings by default, with expandable detailed trigger lists.',
    iconName: 'sparkles' as const,
  },
]

// AfterTide tropes
export const aftertideTropesAndVibes: Trope[] = [
  { name: 'Dark Romance', color: 'bg-red-900', count: 385 },
  { name: 'Monster Romance', color: 'bg-purple-900', count: 142 },
  { name: 'Why Choose', color: 'bg-[#c77dba]', count: 128 },
  { name: 'Captive/Captor', color: 'bg-red-800', count: 116 },
  { name: 'Villain Romance', color: 'bg-gray-800', count: 104 },
  { name: 'Fated Mates', color: 'bg-amber-800', count: 98 },
  { name: 'Enemies to Lovers', color: 'bg-rose-900', count: 94 },
  { name: 'Forced Proximity', color: 'bg-orange-900', count: 87 },
  { name: 'Mafia Romance', color: 'bg-gray-900', count: 82 },
  { name: 'Alien/Human', color: 'bg-blue-900', count: 76 },
  { name: 'Power Exchange', color: 'bg-violet-900', count: 72 },
  { name: 'Breeding Kink', color: 'bg-pink-900', count: 68 },
  { name: 'Age Gap', color: 'bg-rose-800', count: 64 },
  { name: 'Dark Academia', color: 'bg-emerald-900', count: 61 },
  { name: 'Stalker Romance', color: 'bg-red-950', count: 58 },
]

// AfterTide new releases (using aftertide book data format adapted to home page Book type)
export const aftertideNewReleases: Book[] = [
  {
    id: 'at9',
    title: 'Crimson Vows',
    author: 'S.R. Cross',
    coverImage:
      'https://images.unsplash.com/photo-1505628346881-b72b27e84530?w=600&q=80',
    rating: 4.8,
    formats: ['ebook'],
    genres: ['Paranormal Romance', 'Dark Fantasy'],
    isNew: true,
    spiceLevel: 5,
    darknessLevel: 4,
  },
  {
    id: 'at5',
    title: 'Abyssal Depths',
    author: 'Cora Black',
    coverImage:
      'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=600&q=80',
    rating: 4.2,
    formats: ['ebook'],
    genres: ['Monster Romance', 'Sci-Fi'],
    isNew: true,
    spiceLevel: 4,
    darknessLevel: 4,
  },
  {
    id: 'at8',
    title: 'Sweet Surrender',
    author: 'Mia Rose',
    coverImage:
      'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=600&q=80',
    rating: 4.1,
    formats: ['ebook'],
    genres: ['Contemporary Romance', 'Erotica'],
    isNew: true,
    spiceLevel: 3,
    darknessLevel: 1,
  },
  {
    id: 'at11',
    title: 'Starbound',
    author: 'Nova Clarke',
    coverImage:
      'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=600&q=80',
    rating: 4.3,
    formats: ['ebook'],
    genres: ['Sci-Fi Romance', 'Alien Romance'],
    isNew: true,
    spiceLevel: 4,
    darknessLevel: 3,
  },
  {
    id: 'at2',
    title: 'Neon Nights',
    author: 'J.X. Vance',
    coverImage:
      'https://images.unsplash.com/photo-1550684848-fac1c5b4e853?w=600&q=80',
    rating: 4.3,
    formats: ['ebook'],
    genres: ['Sci-Fi Erotica', 'Cyberpunk'],
    isNew: true,
    spiceLevel: 4,
    darknessLevel: 3,
  },
]

// AfterTide debut authors
export const aftertideDebutAuthors: Book[] = [
  {
    id: 'at-debut1',
    title: 'Thorns of Devotion',
    author: 'Raven Blackwell',
    coverImage:
      'https://images.unsplash.com/photo-1614531341773-3bff8b7cb3fc?w=600&q=80',
    rating: 0,
    formats: ['ebook'],
    genres: ['Dark Romance', 'Debut'],
    isNew: true,
    isDebut: true,
    spiceLevel: 4,
    darknessLevel: 4,
  },
  {
    id: 'at-debut2',
    title: 'The Summoning Circle',
    author: 'Hex Moriarty',
    coverImage:
      'https://images.unsplash.com/photo-1519074002996-a69e7ac46a42?w=600&q=80',
    rating: 3.8,
    formats: ['ebook'],
    genres: ['Horror Erotica', 'Debut'],
    isNew: true,
    isDebut: true,
    spiceLevel: 5,
    darknessLevel: 5,
  },
  {
    id: 'at-debut3',
    title: 'Gilded Chains',
    author: 'Ivory Sinclair',
    coverImage:
      'https://images.unsplash.com/photo-1534447677768-be436bb09401?w=600&q=80',
    rating: 4.1,
    formats: ['ebook'],
    genres: ['BDSM Romance', 'Debut'],
    isNew: true,
    isDebut: true,
    spiceLevel: 3,
    darknessLevel: 2,
  },
  {
    id: 'at-debut4',
    title: 'Venom Kiss',
    author: 'Sable Nightshade',
    coverImage:
      'https://images.unsplash.com/photo-1504253163759-c23fccaebb55?w=600&q=80',
    rating: 0,
    formats: ['ebook'],
    genres: ['Shifter Romance', 'Debut'],
    isNew: true,
    isDebut: true,
    spiceLevel: 4,
    darknessLevel: 3,
  },
  {
    id: 'at-debut5',
    title: 'Obsidian Hearts',
    author: 'Crimson Vale',
    coverImage:
      'https://images.unsplash.com/photo-1501163268664-3fdf329d019f?w=600&q=80',
    rating: 4.3,
    formats: ['ebook'],
    genres: ['Mafia Romance', 'Debut'],
    isNew: true,
    isDebut: true,
    spiceLevel: 5,
    darknessLevel: 5,
  },
]

// AfterTide hidden gems
export const aftertideHiddenGems: Book[] = [
  {
    id: 'at12',
    title: 'The Safe Word is Red',
    author: 'Julian Thorne',
    coverImage:
      'https://images.unsplash.com/photo-1513360371669-4adf3dd7dff8?w=600&q=80',
    rating: 4.6,
    formats: ['ebook'],
    genres: ['Erotica', 'Contemporary'],
    reviewCount: 12,
    spiceLevel: 3,
    darknessLevel: 2,
  },
  {
    id: 'at4',
    title: 'Silk & Steel',
    author: 'Marcus Thorne',
    coverImage:
      'https://images.unsplash.com/photo-1534447677768-be436bb09401?w=600&q=80',
    rating: 4.5,
    formats: ['ebook'],
    genres: ['Historical Romance', 'Erotica'],
    reviewCount: 15,
    spiceLevel: 3,
    darknessLevel: 2,
  },
  {
    id: 'at5',
    title: 'Abyssal Depths',
    author: 'Cora Black',
    coverImage:
      'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=600&q=80',
    rating: 4.2,
    formats: ['ebook'],
    genres: ['Monster Romance', 'Sci-Fi'],
    reviewCount: 8,
    spiceLevel: 4,
    darknessLevel: 4,
  },
  {
    id: 'at11',
    title: 'Starbound',
    author: 'Nova Clarke',
    coverImage:
      'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=600&q=80',
    rating: 4.3,
    formats: ['ebook'],
    genres: ['Sci-Fi Romance', 'Alien Romance'],
    reviewCount: 11,
    spiceLevel: 4,
    darknessLevel: 3,
  },
]

// AfterTide spotlight author
export const aftertideSpotlightAuthor: SpotlightAuthor = {
  id: 'sr-cross',
  name: 'S.R. Cross',
  bio: 'Dark fantasy and paranormal romance that explores the shadows of human desire. Creator of the Blood of Oakhaven series.',
  image:
    'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&q=80',
  books: {
    debut: {
      id: 'at1',
      title: 'Velvet Shadows',
      coverImage:
        'https://images.unsplash.com/photo-1614531341773-3bff8b7cb3fc?w=600&q=80',
      genres: ['Paranormal Romance', 'Dark Fantasy'],
      releaseDate: 'Oct 2023',
    },
    mostPopular: {
      id: 'at1',
      title: 'Velvet Shadows',
      coverImage:
        'https://images.unsplash.com/photo-1614531341773-3bff8b7cb3fc?w=600&q=80',
      genres: ['Paranormal Romance', 'Dark Fantasy'],
      rating: 4.6,
      reviewCount: 1245,
    },
    latest: {
      id: 'at9',
      title: 'Crimson Vows',
      coverImage:
        'https://images.unsplash.com/photo-1505628346881-b72b27e84530?w=600&q=80',
      genres: ['Paranormal Romance', 'Dark Fantasy'],
      releaseDate: 'Apr 2024',
      isNew: true,
    },
    leastReviewed: {
      id: 'at9',
      title: 'Crimson Vows',
      coverImage:
        'https://images.unsplash.com/photo-1505628346881-b72b27e84530?w=600&q=80',
      genres: ['Paranormal Romance', 'Dark Fantasy'],
      reviewCount: 850,
    },
  },
  socialLinks: {
    website: 'https://srcross.com',
    instagram: '@srcross_author',
    tiktok: '@srcross_darkromance',
  },
}

export const aftertideSpotlightAuthors: SpotlightAuthor[] = [
  aftertideSpotlightAuthor,
  {
    id: 'at-2',
    name: 'V.E. Thorne',
    bio: 'Bestselling author of gothic horror and dark academia. Exploring the monstrous side of love.',
    image:
      'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&q=80',
    books: {
      debut: {
        id: 'at21',
        title: 'The Ivy House',
        coverImage:
          'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=600&q=80',
        genres: ['Gothic Romance', 'Horror'],
        releaseDate: 'Jan 2020',
      },
      mostPopular: {
        id: 'at22',
        title: 'Blood & Ink',
        coverImage:
          'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?w=600&q=80',
        genres: ['Dark Academia', 'Romance'],
        rating: 4.8,
        reviewCount: 3420,
      },
      latest: {
        id: 'at23',
        title: 'Midnight Library',
        coverImage:
          'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?w=600&q=80',
        genres: ['Dark Academia', 'Horror'],
        releaseDate: 'Nov 2023',
        isNew: true,
      },
      leastReviewed: {
        id: 'at24',
        title: 'Whispers in the Walls',
        coverImage:
          'https://images.unsplash.com/photo-1629992101753-56d196c8aabb?w=600&q=80',
        genres: ['Gothic Horror'],
        reviewCount: 156,
      },
    },
    socialLinks: {
      website: 'https://vethorne.com',
      instagram: '@vethorne',
    },
  },
  {
    id: 'at-3',
    name: 'Lilith Black',
    bio: 'Pushing boundaries in sci-fi romance and monster smut. Where the alien is always the better choice.',
    image:
      'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400&q=80',
    books: {
      debut: {
        id: 'at31',
        title: 'Abducted',
        coverImage:
          'https://images.unsplash.com/photo-1614332287897-cdc485fa562d?w=600&q=80',
        genres: ['Sci-Fi Romance', 'Alien'],
        releaseDate: 'Mar 2021',
      },
      mostPopular: {
        id: 'at32',
        title: "The Warlord's Prize",
        coverImage:
          'https://images.unsplash.com/photo-1589998059171-988d887df646?w=600&q=80',
        genres: ['Sci-Fi Romance', 'Dark Romance'],
        rating: 4.6,
        reviewCount: 2100,
      },
      latest: {
        id: 'at33',
        title: 'Starbound',
        coverImage:
          'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=600&q=80',
        genres: ['Sci-Fi Romance', 'Alien'],
        releaseDate: 'Dec 2023',
        isNew: true,
      },
      leastReviewed: {
        id: 'at34',
        title: 'Deep Space Desire',
        coverImage:
          'https://images.unsplash.com/photo-1518744386442-2d48ac55263f?w=600&q=80',
        genres: ['Sci-Fi Romance', 'Short Story'],
        reviewCount: 95,
      },
    },
    socialLinks: {
      website: 'https://lilithblack.com',
      instagram: '@lilithblackwrites',
    },
  },
]

// AfterTide theme of the week
export const aftertideThemeOfWeek: ThemeOfWeek = {
  theme: 'Monster Romance Week',
  description:
    'Fangs, tentacles, and otherworldly anatomy. Explore stories where the love interest is beautifully inhuman.',
  color: 'bg-gradient-to-r from-[#2d1b3d] to-[#1a1a2e]',
  books: [
    {
      id: 'at5',
      title: 'Abyssal Depths',
      author: 'Cora Black',
      coverImage:
        'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=600&q=80',
      rating: 4.2,
      genres: ['Monster Romance', 'Sci-Fi'],
      spiceLevel: 4,
      darknessLevel: 4,
    },
    {
      id: 'at1',
      title: 'Velvet Shadows',
      author: 'S.R. Cross',
      coverImage:
        'https://images.unsplash.com/photo-1614531341773-3bff8b7cb3fc?w=600&q=80',
      rating: 4.6,
      genres: ['Paranormal Romance', 'Dark Fantasy'],
      spiceLevel: 5,
      darknessLevel: 4,
    },
    {
      id: 'at7',
      title: 'Pack Law',
      author: 'Elena Frost',
      coverImage:
        'https://images.unsplash.com/photo-1504253163759-c23fccaebb55?w=600&q=80',
      rating: 4.4,
      genres: ['Shifter Romance', 'Paranormal'],
      spiceLevel: 4,
      darknessLevel: 4,
    },
    {
      id: 'at3',
      title: 'The Manor',
      author: 'Evelyn Thorne',
      coverImage:
        'https://images.unsplash.com/photo-1519074002996-a69e7ac46a42?w=600&q=80',
      rating: 4.8,
      genres: ['Horror Erotica', 'Gothic'],
      spiceLevel: 5,
      darknessLevel: 5,
    },
  ],
}

// AfterTide reader reviews
export const aftertideReaderReviews: ReaderReview[] = [
  {
    id: 'atr1',
    bookId: 'at1',
    bookTitle: 'Velvet Shadows',
    bookCover:
      'https://images.unsplash.com/photo-1614531341773-3bff8b7cb3fc?w=600&q=80',
    author: 'S.R. Cross',
    reviewerName: 'DarkRomanceReader99',
    rating: 5,
    quote:
      'The power dynamics are perfectly balanced on that knife edge of dubious consent. Malachi is the perfect morally grey MMC.',
    date: '2 days ago',
  },
  {
    id: 'atr2',
    bookId: 'at3',
    bookTitle: 'The Manor',
    bookCover:
      'https://images.unsplash.com/photo-1519074002996-a69e7ac46a42?w=600&q=80',
    author: 'Evelyn Thorne',
    reviewerName: 'HorrorHound',
    rating: 5,
    quote:
      'Genuinely terrifying in parts, incredibly hot in others. The descent into madness is handled so well.',
    date: '1 week ago',
  },
  {
    id: 'atr3',
    bookId: 'at6',
    bookTitle: "The Professor's Pet",
    bookCover:
      'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=600&q=80',
    author: 'L.M. Sterling',
    reviewerName: 'AcademiaAesthetic',
    rating: 4,
    quote:
      'The tension in the classroom scenes is palpable. The praise kink elements are top-tier.',
    date: '5 days ago',
  },
]

// AfterTide featured merch
export const aftertideFeaturedMerch: MerchProduct[] = [
  {
    id: 'atm1',
    title: 'Velvet Shadows Candle',
    vendor: 'Dark Lit Candles',
    image:
      'https://images.unsplash.com/photo-1603006905003-be475563bc59?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=600&q=80',
    price: 28.99,
    relatedBook: 'Velvet Shadows',
    isOfficialMerch: true,
    genre: 'Dark Romance',
  },
  {
    id: 'atm2',
    title: 'Blood of Oakhaven Print',
    vendor: 'Midnight Art Co.',
    image:
      'https://images.unsplash.com/photo-1513364776144-60967b0f800f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=600&q=80',
    price: 34.99,
    relatedBook: 'Velvet Shadows',
    isOfficialMerch: true,
    genre: 'Paranormal',
  },
  {
    id: 'atm3',
    title: 'Tier 5 Reader Mug',
    vendor: 'Dark Lit Merch',
    image:
      'https://images.unsplash.com/photo-1577937927133-66ef06acdf18?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=600&q=80',
    price: 22.99,
    genre: 'Horror',
  },
  {
    id: 'atm4',
    title: 'Monster Romance Bookmark Set',
    vendor: 'Creature Comforts',
    image:
      'https://images.unsplash.com/photo-1456735190827-d1262f71b8a3?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=600&q=80',
    price: 14.99,
    genre: 'Monster Romance',
  },
]

// AfterTide trove articles
export const aftertideTroveArticles: TroveArticle[] = [
  {
    id: 'ata1',
    title: 'Understanding Consent Frameworks in Dark Romance',
    author: 'AfterTide Editorial',
    image:
      'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=600&q=80',
    date: '3 days ago',
    category: 'Guides',
    excerpt:
      'A deep dive into how consent is portrayed in fiction vs. reality, and why clear labeling matters.',
  },
  {
    id: 'ata2',
    title: 'The Rise of Monster Romance: Why Readers Love the Inhuman',
    author: 'Genre Scholar',
    image:
      'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=600&q=80',
    date: '1 week ago',
    category: 'Trends',
    excerpt:
      'From tentacles to vampires, exploring why readers are drawn to love interests that defy human form.',
  },
  {
    id: 'ata3',
    title: 'Writing Dark: How Authors Navigate Tier 4 and 5 Content',
    author: 'Author Spotlight',
    image:
      'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=600&q=80',
    date: '2 weeks ago',
    category: 'Craft',
    excerpt:
      'Three AfterTide authors share their process for writing extreme content responsibly.',
  },
]

// AfterTide upcoming events
export const aftertideUpcomingEvents: UpcomingEvent[] = [
  {
    id: 'ate1',
    title: 'Dark Romance Author Panel',
    date: 'June 10, 2024',
    time: '9:00 PM EST',
    type: 'Virtual',
    description:
      'S.R. Cross, Evelyn Thorne, and Victoria Vane discuss writing the darkest fiction.',
    image:
      'https://images.unsplash.com/photo-1614531341773-3bff8b7cb3fc?w=600&q=80',
  },
  {
    id: 'ate2',
    title: 'Tier System Workshop',
    date: 'June 15, 2024',
    time: '7:00 PM EST',
    type: 'Virtual',
    description:
      'Learn how to accurately tier your dark fiction for the AfterTide catalog.',
    image:
      'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=600&q=80',
  },
  {
    id: 'ate3',
    title: 'Monster Romance Readathon',
    date: 'July 1-7, 2024',
    time: 'All Week',
    type: 'Virtual',
    description:
      'A week-long reading challenge celebrating the best in monster romance.',
    image:
      'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=600&q=80',
  },
]

export const aftertideFeaturedBookBoxes: FeaturedBookBox[] = [
  {
    id: 'atbb1',
    name: 'Dark & Disturbed',
    image:
      'https://images.unsplash.com/photo-1524578271613-d550eacf6090?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=600&q=80',
    theme: 'Dark Romance, Horror, Psychological',
    frequency: 'Monthly',
    price: '42.99',
    currency: 'USD',
    locationCountry: 'United States',
    shipping: 'Worldwide',
    rating: 4.9,
    reviewCount: 156,
    description: 'The darkest fiction with curated dark aesthetic merch.',
  },
  {
    id: 'atbb2',
    name: 'Monster Romance Crate',
    image:
      'https://images.unsplash.com/photo-1495446815901-a7297e633e8d?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=600&q=80',
    theme: 'Monster Romance, Paranormal, Fantasy',
    frequency: 'Monthly',
    price: '38.99',
    currency: 'USD',
    locationCountry: 'United States',
    shipping: 'US & Canada',
    rating: 4.7,
    reviewCount: 98,
    description: 'Creature features with otherworldly collectibles.',
  },
  {
    id: 'atbb3',
    name: 'Forbidden Pages',
    image:
      'https://images.unsplash.com/photo-1457369804613-52c61a468e7d?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=600&q=80',
    theme: 'Taboo, Dark Academia, Gothic',
    frequency: 'Bi-Monthly',
    price: '49.99',
    currency: 'USD',
    locationCountry: 'United Kingdom',
    shipping: 'Worldwide',
    rating: 4.8,
    reviewCount: 72,
    description: 'Boundary-pushing fiction with gothic-inspired items.',
  },
]
